import React, {useEffect, useMemo, useState} from 'react'
import {
	Box,
	Button,
	Chip,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	useTheme,
} from '@mui/material'
import {doGet} from '../../util/do-fetch'

/*
 * "Fill from real data" for the prompt preview (090).
 *
 * Typing a plausible question into a preview box tests the prompt against fiction. This pulls a
 * real product, a real topic and its real questions, then derives every placeholder the prompts
 * know about from them -- so what you copy into a sandbox is what the model would actually see.
 *
 * Everything comes from two endpoints that already exist: /admin/products for the list, and
 * /admin/products/:id for the topic tree with its questions attached.
 */

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F']

// the topic tree arrives nested under `topics`; the pickers want a flat list carrying the full path
const flattenTopics = (topics, trail = []) => {
	if (!topics) return []
	return topics.flatMap(topic => {
		const path = [...trail, topic.name]
		return [
			{id: topic.id, name: topic.name, path: topic.path || path.join(' > '), depth: trail.length, questions: topic.questions || []},
			...flattenTopics(topic.topics, path),
		]
	})
}

const labelFor = (question, index) => {
	const option = question.options && question.options[index]
	return option ? `${LETTERS[index]}. ${option.label}` : ''
}

const PromptSampleData = ({onFill}) => {
	const theme = useTheme()

	const [products, setProducts] = useState([])
	const [productId, setProductId] = useState('')
	const [topics, setTopics] = useState([])
	const [topicId, setTopicId] = useState('')
	const [count, setCount] = useState(5)
	const [loading, setLoading] = useState(false)

	const [answeredIndex, setAnsweredIndex] = useState(0)
	const [chosenOption, setChosenOption] = useState(null)
	const [results, setResults] = useState([])   // one true/false per pulled question

	useEffect(() => {
		doGet('/admin/products')
			.then(response => (response.ok ? response.json() : []))
			.then(data => setProducts(Array.isArray(data) ? data : (data.products || [])))
			.catch(() => setProducts([]))
	}, [])

	useEffect(() => {
		if (!productId) return
		setLoading(true)
		setTopics([])
		setTopicId('')

		doGet(`/admin/products/${productId}`)
			.then(response => (response.ok ? response.json() : null))
			.then(product => setTopics(flattenTopics(product && product.topics).filter(t => t.questions.length)))
			.catch(() => setTopics([]))
			.finally(() => setLoading(false))
	}, [productId])

	const topic = topics.find(t => String(t.id) === String(topicId))
	const product = products.find(p => String(p.id) === String(productId))

	const questions = useMemo(
		() => (topic ? topic.questions.slice(0, Math.max(1, Number(count) || 1)) : []),
		[topic, count]
	)

	// a fresh pull starts as all-correct with the first question answered correctly; the admin
	// changes whichever of those they want to see the prompt react to
	useEffect(() => {
		setResults(questions.map(() => true))
		setAnsweredIndex(0)
		setChosenOption(questions.length ? questions[0].answer : null)
	}, [topicId, count]) // eslint-disable-line react-hooks/exhaustive-deps

	const answered = questions[answeredIndex]
	const isCorrect = answered ? chosenOption === answered.answer : null

	/*
	 * The parent section, derived the way routes/mini.js derives it for the real prompt: the
	 * second-to-last part of the path. Real paths are "/Domain/Topic"; flattenTopics falls back to
	 * a " > " join when a topic carries no path, so both separators are handled.
	 */
	const sectionNameOf = (t) => {
		const raw = String((t && t.path) || '')
		const parts = (raw.includes('/') ? raw.split('/') : raw.split('>')).map(p => p.trim()).filter(p => p)
		return parts.length > 1 ? parts[parts.length - 2] : ''
	}

	// the option a wrong answer landed on: the first one that is not the correct one
	const wrongOptionFor = (q) => {
		const index = (q.options || []).findIndex((o, i) => i !== q.answer)
		return index === -1 ? q.answer : index
	}

	/*
	 * One question, in the shape topicReviewPrompt actually builds (101): every option, a marker on
	 * the correct one, and what the student chose. This was a single line per question, which
	 * understated the real block by roughly six times -- and an admin writing "refer to the
	 * options they were given" has to be able to see that the options are in there.
	 *
	 * The sample answers every question, so nothing here is tagged SKIPPED. The runtime tags one
	 * when a summary covers a question the student never attempted.
	 */
	const describeQuestion = (q, i, correct) => {
		const lines = [`${i + 1}. ${q.question}`]

		;(q.options || []).forEach((o, index) => {
			lines.push(`   ${LETTERS[index]}. ${o.label}${index === q.answer ? '   <- correct answer' : ''}`)
		})

		lines.push(`   Student answered: [${correct ? 'CORRECT' : 'INCORRECT'}] ${labelFor(q, correct ? q.answer : wrongOptionFor(q))}`)

		return lines.join('\n')
	}

	const fill = () => {
		if (!product || !topic || !questions.length) return

		const correctCount = results.filter(Boolean).length
		/*
		 * Two percentages that differ at runtime and agree here: accuracy_percent is correct over
		 * ANSWERED and percent_correct is correct over TOTAL, and this sample answers every
		 * question. Both are filled anyway, so a prompt using either previews with a real number
		 * rather than a literal {accuracy_percent}.
		 */
		const percentCorrect = Math.round((correctCount / questions.length) * 100)
		const values = {
			product_name: product.name,
			prepkit_name: product.name,
			prompt_name: product.name,
			topic: product.name,
			topic_name: topic.name,
			topic_path: topic.path,
			section_name: sectionNameOf(topic),
			question_count: String(questions.length),
			correct_count: String(correctCount),
			answered_count: String(questions.length),
			skipped_count: '0',
			percent_correct: String(percentCorrect),
			accuracy_percent: String(percentCorrect),
			percent_correct_attempted: String(percentCorrect),
			questions: questions.map((q, i) => describeQuestion(q, i, results[i])).join('\n\n'),
			existing_questions: questions.map((q, i) => `${i + 1}. ${q.question}`).join('\n'),
		}

		if (answered) {
			values.correct_answer = labelFor(answered, answered.answer)
			values.user_selected_answer = chosenOption == null ? '' : labelFor(answered, chosenOption)
		}

		onFill(values)
	}

	return (
		<Box sx={{
			border: `1px solid ${theme.palette.divider}`,
			borderRadius: 1,
			p: 2,
			mb: 2,
			backgroundColor: theme.palette.action.hover,
		}}>
			<Typography variant="subtitle2" sx={{mb: 1}}>Fill from real data</Typography>

			<FormControl fullWidth size="small" sx={{mb: 1.5}}>
				<InputLabel>Product</InputLabel>
				<Select label="Product" value={productId} onChange={(e) => setProductId(e.target.value)}>
					{products.map(p => (
						<MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
					))}
				</Select>
			</FormControl>

			<FormControl fullWidth size="small" sx={{mb: 1.5}} disabled={!topics.length}>
				<InputLabel>{loading ? 'Loading topics…' : 'Topic'}</InputLabel>
				<Select label={loading ? 'Loading topics…' : 'Topic'}
				        value={topicId}
				        onChange={(e) => setTopicId(e.target.value)}>
					{topics.map(t => (
						<MenuItem key={t.id} value={t.id} sx={{pl: 2 + t.depth * 2}}>
							{t.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ml: 1}}>
								({t.questions.length})
							</Typography>
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{topic && (
				<>
					<TextField
						fullWidth
						size="small"
						type="number"
						label="Questions to pull"
						value={count}
						onChange={(e) => setCount(e.target.value)}
						inputProps={{min: 1, max: topic.questions.length}}
						helperText={`${topic.questions.length} available in this topic`}
						sx={{mb: 1.5}}
					/>

					{/* which question the student is looking at -- the one correct_answer and
					    user_selected_answer describe */}
					<FormControl fullWidth size="small" sx={{mb: 1.5}}>
						<InputLabel>Question they are on</InputLabel>
						<Select label="Question they are on"
						        value={answeredIndex}
						        onChange={(e) => {
							        setAnsweredIndex(e.target.value)
							        setChosenOption(questions[e.target.value].answer)
						        }}>
							{questions.map((q, i) => (
								<MenuItem key={q.id} value={i}>
									{i + 1}. {q.question.slice(0, 70)}{q.question.length > 70 ? '…' : ''}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					{answered && (
						<Box sx={{mb: 1.5}}>
							<FormControl fullWidth size="small">
								<InputLabel>Their answer</InputLabel>
								<Select label="Their answer"
								        value={chosenOption == null ? '' : chosenOption}
								        onChange={(e) => setChosenOption(e.target.value)}>
									{answered.options.map((o, i) => (
										<MenuItem key={i} value={i}>
											{LETTERS[i]}. {o.label}
											{i === answered.answer && (
												<Typography component="span" variant="caption" color="success.main" sx={{ml: 1}}>
													correct
												</Typography>
											)}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							{/* derived, never chosen: the question already knows which option is right,
							    so letting both be set independently would only allow them to disagree */}
							<Chip size="small"
							      sx={{mt: 1}}
							      color={isCorrect ? 'success' : 'error'}
							      label={isCorrect ? 'Answered correctly' : 'Answered incorrectly'}
							/>
						</Box>
					)}

					{/* the per-question tally the topic summary reads: {questions}, {correct_count},
					    {percent_correct} */}
					<Typography variant="caption" color="text.secondary">Results for the topic summary</Typography>
					<Box sx={{maxHeight: 160, overflow: 'auto', mt: .5, mb: 1.5}}>
						{questions.map((q, i) => (
							<Box key={q.id} display="flex" alignItems="center" gap={1} sx={{mb: .5}}>
								{/*
								  * Colour carries the state here, not just which button looks pressed --
								  * a column of small toggles is read as a pattern of right and wrong, and
								  * that is unreadable if both selected states are the same grey.
								  */}
								<ToggleButtonGroup
									size="small"
									exclusive
									value={results[i] ? 'correct' : 'incorrect'}
									onChange={(e, v) => {
										if (!v) return
										const next = [...results]
										next[i] = v === 'correct'
										setResults(next)
									}}
								>
									<ToggleButton value="correct" sx={{
										py: 0, px: 1, fontSize: '.7rem',
										'&.Mui-selected': {
											color: '#fff',
											backgroundColor: theme.custom.status.greenStrong,
											'&:hover': {backgroundColor: theme.custom.status.greenStrong},
										},
									}}>✓</ToggleButton>
									<ToggleButton value="incorrect" sx={{
										py: 0, px: 1, fontSize: '.7rem',
										'&.Mui-selected': {
											color: '#fff',
											backgroundColor: theme.custom.status.danger,
											'&:hover': {backgroundColor: theme.custom.status.danger},
										},
									}}>✗</ToggleButton>
								</ToggleButtonGroup>
								<Typography variant="caption" noWrap sx={{flex: 1, minWidth: 0}}>
									{i + 1}. {q.question}
								</Typography>
							</Box>
						))}
					</Box>

					<Button variant="contained" size="small" onClick={fill} disabled={!questions.length}>
						Fill values
					</Button>
				</>
			)}
		</Box>
	)
}

export default PromptSampleData
